import { ServiceRegistration } from "@/components/booking/create/ServiceRegistration";
import { NotificationBanner } from "@/components/common/NotificationBanner";
import { HomeHeroCard } from "@/components/customer/HomeHeroCard";
import { ThemedText } from "@/components/themed-text";
import { RecoveryButton } from "@/components/ui/recovery-button";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useUnreadNotificationsCount } from "@/hooks/queries/use-notifications-query";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { useColorScheme } from "@/hooks/use-color-scheme";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useFocusEffect, useRouter } from "expo-router";
import React, { useCallback } from "react";
import { ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import { ScreenHeader } from "../common/ScreenHeader";

export default function CustomerHome() {
  const router = useRouter();
  const inset = useSafeAreaInsets();
  const { theme } = useAuth();
  const desktop = useDesktopLayout();
  const colorScheme = useColorScheme();
  const backgroundColor =
    desktop && colorScheme !== "dark" ? BrandColors.backgroundGray : theme.background;
  const { data: unreadCount, refetch: refetchUnreadCount } =
    useUnreadNotificationsCount();

  useFocusEffect(
    useCallback(() => {
      refetchUnreadCount({ cancelRefetch: false });
    }, [refetchUnreadCount]),
  );

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor }]}
      edges={desktop ? ["bottom"] : ["top", "bottom"]}
    >
      {desktop ? null : (
      <ScreenHeader
        title=""
        rightComponent={
          <TouchableOpacity
            style={[styles.iconButton, { backgroundColor: theme.border }]}
            onPress={() => router.push("/(tabs)/notifications")}
            accessibilityRole="button"
            accessibilityLabel={
              unreadCount
                ? `Notifications, ${unreadCount} unread`
                : "Notifications"
            }
          >
            <Ionicons
              name="notifications-outline"
              size={22}
              color={theme.text}
            />
            {unreadCount ? (
              <View style={[styles.badge, { borderColor: theme.background }]}>
                <ThemedText style={styles.badgeText} type="extraSmallSemiBold">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </ThemedText>
              </View>
            ) : null}
          </TouchableOpacity>
        }
      />
      )}
      <NotificationBanner />
      <ScrollView
        contentContainerStyle={[
          styles.scrollContent,
          desktop && styles.desktopScrollContent,
        ]}
        showsVerticalScrollIndicator={false}
      >
        <HomeHeroCard />
        <ServiceRegistration />
      </ScrollView>
      <RecoveryButton
        style={{ marginHorizontal: 20, marginBottom: inset.bottom }}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingBottom: 40,
  },
  desktopScrollContent: {
    paddingHorizontal: 32,
    paddingTop: 24,
  },
  iconButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  badge: {
    position: "absolute",
    top: -2,
    right: -2,
    minWidth: 20,
    height: 20,
    paddingHorizontal: 5,
    borderRadius: 10,
    borderWidth: 2,
    backgroundColor: BrandColors.error,
    alignItems: "center",
    justifyContent: "center",
  },
  badgeText: {
    color: BrandColors.white,
    fontSize: 10,
    lineHeight: 12,
  },
});
